import { Injectable, signal, inject } from "@angular/core";
import { ActivatedRouteSnapshot, NavigationEnd, Router } from "@angular/router";
import { Observable, of, firstValueFrom } from "rxjs";
import { filter } from "rxjs/operators";
import { FmsService } from "./fms.service";
import { BreadcrumbItem } from "@storeblocks/breadcrumbs-ng";
import { routes, RouteKey } from "src/app/pages/articles-page/routes";

// Constants for the first breadcrumb item
const OVERVIEW_BREADCRUMB = {
  title: "breadcrumbs.overview",
  href: "breadcrumbs.overview.url",
};

@Injectable({ providedIn: "root" })
export class BreadcrumbsService {
  private readonly router = inject(Router);
  private readonly fmsService = inject(FmsService);

  public breadcrumbItems = signal<BreadcrumbItem[]>([OVERVIEW_BREADCRUMB]);

  constructor() {
    this.updateBreadcrumbItems();

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(async () => {
      await this.updateBreadcrumbItems();
    });
  }

  private async updateBreadcrumbItems(): Promise<void> {
    const breadcrumbItems = await firstValueFrom(this.resolveBreadcrumbItems(this.router.routerState.snapshot.root));
    const translatedItems: BreadcrumbItem[] = [];

    // Always start with the overview breadcrumb (translated)
    const overviewTitle = this.fmsService.getText(OVERVIEW_BREADCRUMB.title);
    const overviewUrl = this.fmsService.getText(OVERVIEW_BREADCRUMB.href);
    translatedItems.push({
      title: overviewTitle,
      href: overviewUrl,
      queryParamsHandling: "merge",
    });

    // Translate other breadcrumb items
    for (const item of breadcrumbItems) {
      const isArticle = item.to?.includes(`${routes[RouteKey.Root]}/${routes[RouteKey.Article]}`);
      const translatedTitle = isArticle ? item.title : this.fmsService.getText(item.title);

      translatedItems.push({
        ...item,
        title: translatedTitle,
        queryParamsHandling: "merge",
      });
    }

    this.breadcrumbItems.set(translatedItems);
  }

  private resolveBreadcrumbItems(
    route: ActivatedRouteSnapshot | null,
    url = "",
    breadcrumbItems: BreadcrumbItem[] = [],
  ): Observable<BreadcrumbItem[]> {
    if (!route) {
      return of(breadcrumbItems);
    }

    const pathSegments = route.url.map((segment) => segment.path);
    const nextUrl = `${url}/${pathSegments.join("/")}`.replace(/\/$/, "") || "/";

    if (route.routeConfig && route.data?.["breadcrumb"]) {
      const breadcrumbLabel = route.data["breadcrumb"];

      if (breadcrumbLabel && breadcrumbLabel !== "skip-breadcrumb") {
        // Special case for breadcrumbs.smartPension - always use "/"
        const finalUrl = breadcrumbLabel === "breadcrumbs.smartPension" ? "/" : nextUrl;

        const isDuplicate = breadcrumbItems.some((item) => item.to === finalUrl);
        if (!isDuplicate) {
          breadcrumbItems.push({
            title: breadcrumbLabel,
            to: finalUrl,
          });
        }
      }
    }

    return route.firstChild
      ? this.resolveBreadcrumbItems(route.firstChild, nextUrl, breadcrumbItems)
      : of(breadcrumbItems);
  }
}
